import { useEffect, useRef, useState } from 'react';
import { Box, Divider, Flex, Text } from '@chakra-ui/react';
import { Accordion, ButtonText } from '@morressier/alembic';
import { PdfViewerContainer } from 'src/components/PdfViewer/PdfViewerContainer';
import { countReviewFiles } from 'src/utils/fileCount';
import { SubmissionDetails } from './SubmissionDetailsTab';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import { SubmissionFilesDetails } from './SubmissionFilesDetails';
import ReviewerInfo from 'src/components/Submission/MySubmission/components/ReviewerInfo';

const SCROLL_CONTAINER_ID = 'review-left-column';
// Breathing room below the sticky nav when scrolling to a section.
const NAV_GAP = 8;
// react-pdf paints its text layer (and annotation layer) above the page canvas
// with z-index 2/3. Those spans are transparent, so a lower z-index here leaves
// the nav looking fine but with its clicks swallowed by the PDF beneath it.
const NAV_Z_INDEX = 4;
// Allow the accordion open animation to settle before measuring the section.
const ACCORDION_SETTLE_MS = 300;
export const REVIEW_SECTION_IDS = {
  manuscript: 'review-manuscript',
  submissionDetails: 'review-submission-details',
  submissionFiles: 'review-submission-files',
  authorInfo: 'author-information'
} as const;

const ReviewSubmissionTab = ({
  fields,
  isDoubleAnonymous,
  enableConsolidatedPdf
}: {
  fields: ISubmissionFieldsResponse;
  isDoubleAnonymous: boolean;
  enableConsolidatedPdf?: boolean;
}) => {
  const [isManuscriptOpen, setIsManuscriptOpen] = useState(true);
  const [isSubmissionDetailsOpen, setIsSubmissionDetailsOpen] = useState(false);
  const [isAuthorInfoOpen, setIsAuthorInfoOpen] = useState(false);
  const [isSubmissionFilesOpen, setIsSubmissionFilesOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<number | null>(null);
  const totalFileCount = countReviewFiles(fields);
  const getNavOffset = () => (navRef.current?.offsetHeight ?? 0) + NAV_GAP;

  const scrollToSection = (id: string) => {
    const container = document.getElementById(SCROLL_CONTAINER_ID);
    const target = document.getElementById(id);
    if (!container || !target) return;

    const y =
      target.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      getNavOffset();

    container.scrollTo({ top: Math.max(y, 0), behavior: 'smooth' });
  };

  const navigateToSection = (id: string) => {
    const needsAccordion =
      id === REVIEW_SECTION_IDS.submissionDetails ||
      id === REVIEW_SECTION_IDS.submissionFiles;

    // Accordion panels can still be animating even after their open state has
    // updated. Always wait for the panel to settle before measuring a target
    // inside it, otherwise a quick second navigation can use a stale position.
    if (needsAccordion) {
      if (id === REVIEW_SECTION_IDS.submissionDetails) {
        setIsSubmissionDetailsOpen(true);
      } else {
        setIsSubmissionFilesOpen(true);
      }
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = window.setTimeout(
        () => scrollToSection(id),
        ACCORDION_SETTLE_MS
      );
      return;
    }

    scrollToSection(id);
  };

  useEffect(
    () => () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    },
    []
  );

  return (
    <>
      {/* Horizontal jump-to navigation */}
      <Flex
        ref={navRef}
        position="sticky"
        top={0}
        zIndex={NAV_Z_INDEX}
        bg="white"
        py={2}
        borderColor="gray.200"
      >
        <Flex mb={2}>
          <Flex flexDir={'row'} alignItems={'center'}>
            <Text textStyle="headingItem">Submission Details:</Text>
            <ButtonText
              onClick={() =>
                navigateToSection(REVIEW_SECTION_IDS.submissionDetails)
              }
            >
              View
            </ButtonText>
          </Flex>
          {!enableConsolidatedPdf && (
            <>
              <Divider
                orientation="vertical"
                flex="1"
                mx={2}
                borderColor="gray.300"
              />
              <Flex flexDir={'row'} alignItems={'center'}>
                <Text textStyle="headingItem">Submission Files:</Text>
                <ButtonText
                  onClick={() =>
                    navigateToSection(REVIEW_SECTION_IDS.submissionFiles)
                  }
                >
                  {totalFileCount}
                </ButtonText>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>

      <Flex flexDirection="column" gap={6}>
        <Accordion
          chakraAccordionProps={{
            width: '100%',
            index: isManuscriptOpen ? [0] : [],
            onChange: expandedIndex =>
              setIsManuscriptOpen(
                Array.isArray(expandedIndex)
                  ? expandedIndex.length > 0
                  : expandedIndex === 0
              )
          }}
          items={[
            {
              title: <Text textStyle="headingItem">Manuscript</Text>,
              content: (
                <Box id={REVIEW_SECTION_IDS.manuscript}>
                  <PdfViewerContainer
                    fileUrl={
                      enableConsolidatedPdf
                        ? (fields['AnonymizedConsolidatedPdf']?.value as any)
                            ?.url
                        : (fields['Manuscript']?.value as any)?.url
                    }
                    fileExtension={
                      enableConsolidatedPdf
                        ? (fields['AnonymizedConsolidatedPdf']?.value as any)
                            ?.extension
                        : (fields['Manuscript']?.value as any)?.extension
                    }
                  />
                </Box>
              )
            }
          ]}
        />

        <Accordion
          chakraAccordionProps={{
            width: '100%',
            index: isSubmissionDetailsOpen ? [0] : [],
            onChange: expandedIndex =>
              setIsSubmissionDetailsOpen(
                Array.isArray(expandedIndex)
                  ? expandedIndex.length > 0
                  : expandedIndex === 0
              )
          }}
          items={[
            {
              title: <Text textStyle="headingItem">Submission Details</Text>,
              content: (
                <Box id={REVIEW_SECTION_IDS.submissionDetails}>
                  <SubmissionDetails />
                </Box>
              )
            }
          ]}
        />

        {!isDoubleAnonymous && (
          <Accordion
            chakraAccordionProps={{
              width: '100%',
              index: isAuthorInfoOpen ? [0] : [],
              onChange: expandedIndex =>
                setIsAuthorInfoOpen(
                  Array.isArray(expandedIndex)
                    ? expandedIndex.length > 0
                    : expandedIndex === 0
                )
            }}
            items={[
              {
                title: <Text textStyle="headingItem">Author Information</Text>,
                content: (
                  <Box id={REVIEW_SECTION_IDS.authorInfo}>
                    <ReviewerInfo />
                  </Box>
                )
              }
            ]}
          />
        )}
        {!enableConsolidatedPdf && (
          <Accordion
            chakraAccordionProps={{
              width: '100%',
              index: isSubmissionFilesOpen ? [0] : [],
              onChange: expandedIndex =>
                setIsSubmissionFilesOpen(
                  Array.isArray(expandedIndex)
                    ? expandedIndex.length > 0
                    : expandedIndex === 0
                )
            }}
            items={[
              {
                title: (
                  <Text textStyle="headingItem">
                    Submission Files ({totalFileCount})
                  </Text>
                ),
                content: (
                  <Box id={REVIEW_SECTION_IDS.submissionFiles}>
                    <SubmissionFilesDetails />
                  </Box>
                )
              }
            ]}
          />
        )}
      </Flex>
    </>
  );
};

export default ReviewSubmissionTab;
